/*
 * Copyright (c) CIRROM 2023.
 */

import {
  getChangeStatsOverTime,
  IntegratedSystem,
  IntegratedSystemDetail,
  PendingChangesSummary,
  TicketsTimeline,
  TrendItem,
} from "../../api/integratedsystems";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { isEmpty } from "lodash";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import {
  BusinessOutlined,
  EmailOutlined,
  HelpOutlined,
  LocationOnOutlined,
  SwitchAccountOutlined,
  TrendingDownOutlined,
  TrendingFlatOutlined,
  TrendingUpOutlined,
} from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { a, useSpring } from "@react-spring/web";
import Box from "@mui/material/Box";
import classes from "./NewCRMPanel.module.css";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import { NUMBER_FORMATTER } from "../utils/utils";
import Tooltip from "@mui/material/Tooltip";
import { NewCRMPanelBarSection } from "./CRMPanelBarSection";
import { GridLegacy } from "@mui/material";

export function TicketBox(
  props: Readonly<{
    category: string;
    categoryNumber: number;
    icon: any;
    tooltip: string;
    navigatePath: string;
    isLoading: boolean;
    trend?: TrendItem;
    showDistinctContacts: boolean;
    dataProjectId: number;
    systemId: number;
  }>
) {
  /**
   * Outstanding tickets in a given data quality category
   * */
  const navigate = useNavigate();

  const [clicked, setClicked] = useState<boolean>(false);
  const [showGraph, setShowGraph] = useState<boolean>(false);
  const [graphData, setGraphData] = useState<TicketsTimeline[]>();

  const computeTrend = () => {
    /*Compute trend locally based on category number and a week ago's data
     * */
    if (!props.trend) {
      return 0;
    }
    if (!props.showDistinctContacts) {
      if (props?.trend?.old && props.categoryNumber) {
        return Math.round(
          ((props.categoryNumber - props.trend.old) / props.trend.old) * 100
        );
      }
      if (props?.trend?.old && !props.categoryNumber) {
        return -100;
      }
      if (!props?.trend?.old && !props.categoryNumber) {
        return 0;
      }
      if (!props?.trend?.old && props.categoryNumber) {
        return 100;
      }
    } else {
      return props.trend.trend;
    }
  };

  const computedTrend = computeTrend();

  const { transform, opacity } = useSpring({
    opacity: clicked || showGraph ? 0 : 1,
    transform: `perspective(600px) rotateX(${
      clicked ? 360 : showGraph ? 180 : 0
    }deg)`,

    config: { mass: 5, tension: 250, friction: 50 },
    onResolve: () => clicked && navigate(props.navigatePath),
  });

  useEffect(() => {
    if (showGraph) {
      getChangeStatsOverTime(
        props.category.toUpperCase(),
        props.systemId,
        props.dataProjectId
      )
        .then((response) => {
          setGraphData(response);
        })
        .catch((err) => console.log(err));
    }
  }, [showGraph, props.category, props.systemId, props.dataProjectId]);
  return (
    <GridLegacy item xs={15} sm={7} md={4} lg={3} xl={3}>
      <a.div
        style={{
          transform,
          opacity: showGraph && opacity,
          display:
            showGraph && opacity.to((o) => `${o < 0.65 ? "none" : "inherit"}`),
        }}
      >
        <Box
          className={classes.crmPanelTicketBox}
          onClick={(e) => setClicked(true)}
        >
          <div className={classes.crmPanelTicketBoxHeader}>
            <Typography variant={"h3"} style={{ fontWeight: "bold" }}>
              {props.category}
            </Typography>
            <IconButton
              className={classes.crmPanelTicketBoxIcon}
              disableRipple
              disableTouchRipple
            >
              {props.icon}
            </IconButton>
          </div>
          <div className={classes.crmPanelTicketBoxFact}>
            {props.isLoading ? (
              <CircularProgress />
            ) : (
              <Typography variant={"h1"} style={{ fontWeight: "bold" }}>
                {NUMBER_FORMATTER.format(props.categoryNumber || 0)}
              </Typography>
            )}
          </div>
          <div className={classes.crmPanelTicketBoxTrend}>
            {props.trend !== undefined && (
              <>
                {Math.round(computedTrend) < 0 ? (
                  <TrendingDownOutlined
                    style={{ height: "35px", width: "35px", color: "#35BE5E" }}
                  />
                ) : Math.round(computedTrend) === 0 ? (
                  <TrendingFlatOutlined
                    style={{ height: "35px", width: "35px", color: "#ffbf23" }}
                  />
                ) : (
                  <TrendingUpOutlined
                    style={{ height: "35px", width: "35px", color: "#E50044" }}
                  />
                )}

                <Typography
                  variant={"h4"}
                  style={{
                    fontWeight: "bold",
                    color: `${
                      Math.round(computedTrend) < 0
                        ? "#35BE5E"
                        : Math.round(computedTrend) === 0
                        ? "#ffbf23"
                        : "#E50044"
                    }`,
                  }}
                >
                  {Math.round(computedTrend)}%
                </Typography>
                <Typography
                  variant={"h4"}
                  style={{
                    fontWeight: "bold",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  Since last week
                </Typography>
              </>
            )}
          </div>
          <div className={classes.crmPanelMoreInfo}>
            <Typography
              variant={"h5"}
              style={{
                fontWeight: "bold",
                color: "#777776",
                cursor: "pointer",
              }}
              onClick={(e) => {
                setShowGraph(true);
                e.stopPropagation();
              }}
            >
              View More
            </Typography>
            <Tooltip title={props.tooltip}>
              <HelpOutlined
                style={{ width: "22px", height: "22px", color: "#777776" }}
              />
            </Tooltip>
          </div>
        </Box>
      </a.div>
      {showGraph && (
        <a.div
          style={{
            opacity: opacity.to((o) => 1 - o),
            transform: transform,
            display: opacity.to((o) => `${o >= 0.65 ? "none" : "inherit"}`),
            rotateX: clicked ? "360deg" : "180deg",
          }}
        >
          <Box
            className={classes.crmPanelTicketBox}
            onClick={(e) => setClicked(true)}
          >
            <div
              className={classes.crmPanelTicketBoxHeader}
              style={{ marginBottom: "-10px" }}
            >
              <Typography variant={"h3"} style={{ fontWeight: "bold" }}>
                {props.category}
              </Typography>
              <IconButton
                className={classes.crmPanelTicketBoxIcon}
                disableRipple
                disableTouchRipple
              >
                {props.icon}
              </IconButton>
            </div>
            <div
              className={classes.crmPanelTicketBoxFact}
              style={{ height: "calc(100% - 50px)" }}
            >
              {graphData && (
                <NewCRMPanelBarSection
                  changeMetrics={graphData.map((entry) => {
                    return {
                      ...entry,
                      readableMonth: new Date(entry.date).toLocaleDateString(
                        "en-us",
                        {
                          year: "2-digit",
                          month: "short",
                        }
                      ),
                    };
                  })}
                  series={["total_issues"]}
                  maxHeight={150}
                />
              )}
              {!graphData && (
                <div
                  style={{
                    height: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <CircularProgress />
                </div>
              )}
            </div>
            <div
              className={classes.crmPanelMoreInfo}
              style={{ marginTop: "-15px" }}
            >
              <Typography
                variant={"h5"}
                style={{
                  fontWeight: "bold",
                  color: "#777776",
                  cursor: "pointer",
                }}
                onClick={(e) => {
                  setShowGraph(false);
                  e.stopPropagation();
                }}
              >
                Tickets
              </Typography>
            </div>
          </Box>
        </a.div>
      )}
    </GridLegacy>
  );
}

export function AllTicketBoxes(
  props: Readonly<{
    systemDetails: IntegratedSystemDetail;
    system: IntegratedSystem;
    dataProjectId: number;
    pendingChangesSummary?: PendingChangesSummary;
    showDistinctContacts: boolean;
  }>
) {
  /**
   * List of panels displaying outstanding tickets in all the cirrom data quality categories
   * */
  const theme = useTheme();
  const lgDownStyle = useMediaQuery(theme.breakpoints.down("xl"));
  const iconStyle = {
    height: lgDownStyle ? "20px" : "30px",
    width: lgDownStyle ? "20px" : "30px",
  };

  return (
    <>
      <TicketBox
        category={"Name"}
        isLoading={isEmpty(props.pendingChangesSummary)}
        categoryNumber={
          props.pendingChangesSummary &&
          props.pendingChangesSummary.first_name +
            props.pendingChangesSummary.last_name +
            props.pendingChangesSummary.salutation
        }
        icon={<BadgeOutlinedIcon style={iconStyle} />}
        tooltip={"Total first name and last name issues identified by Cirrom"}
        navigatePath={
          props.dataProjectId
            ? `/app/data-project/name-changes/${props.dataProjectId}`
            : `/app/crm/name-changes/${props.system.id}`
        }
        trend={props.systemDetails.trend.name}
        showDistinctContacts={props.showDistinctContacts}
        dataProjectId={props.dataProjectId}
        systemId={props.system?.id}
      />
      <TicketBox
        category={"Email"}
        categoryNumber={props?.pendingChangesSummary?.email}
        isLoading={isEmpty(props.pendingChangesSummary)}
        icon={<EmailOutlined style={iconStyle} />}
        tooltip={
          "Total email issues identified by Cirrom. This includes incorrect emails, personal email boxes, no mail server addresses etc"
        }
        navigatePath={
          props.dataProjectId
            ? `/app/data-project/email-changes/${props.dataProjectId}`
            : `/app/crm/email-changes/${props.system.id}`
        }
        trend={props.systemDetails.trend.email}
        showDistinctContacts={props.showDistinctContacts}
        dataProjectId={props.dataProjectId}
        systemId={props.system?.id}
      />
      <TicketBox
        category={"Location"}
        isLoading={isEmpty(props.pendingChangesSummary)}
        categoryNumber={
          props.pendingChangesSummary &&
          props.pendingChangesSummary.city +
            props.pendingChangesSummary.country +
            props.pendingChangesSummary.stateorprovince +
            props.pendingChangesSummary.address_linkage +
            props.pendingChangesSummary.telephone
        }
        icon={<LocationOnOutlined style={iconStyle} />}
        tooltip={
          "Total incorrect or missing location identifiers. This includes city, country and state/province"
        }
        navigatePath={
          props.dataProjectId
            ? `/app/data-project/location-changes/${props.dataProjectId}`
            : `/app/crm/location-changes/${props.system.id}`
        }
        trend={props.systemDetails.trend.location}
        showDistinctContacts={props.showDistinctContacts}
        dataProjectId={props.dataProjectId}
        systemId={props.system?.id}
      />
      <TicketBox
        category={"Employment"}
        isLoading={isEmpty(props.pendingChangesSummary)}
        categoryNumber={
          props.pendingChangesSummary &&
          props.pendingChangesSummary.company +
            props.pendingChangesSummary.job_title
        }
        icon={<BusinessOutlined style={iconStyle} />}
        tooltip={
          "Total issues with job titles and incorrect account categorization"
        }
        navigatePath={
          props.dataProjectId
            ? `/app/data-project/employment-changes/${props.dataProjectId}`
            : `/app/crm/employment-changes/${props.system.id}`
        }
        trend={props.systemDetails.trend.employment}
        showDistinctContacts={props.showDistinctContacts}
        dataProjectId={props.dataProjectId}
        systemId={props.system?.id}
      />
      <TicketBox
        category={"Duplicates"}
        isLoading={isEmpty(props.pendingChangesSummary)}
        categoryNumber={props.systemDetails.merged_contacts.tickets}
        icon={<SwitchAccountOutlined style={iconStyle} />}
        tooltip={"Total number of duplicate contacts identified by Cirrom"}
        navigatePath={
          props.dataProjectId
            ? `/app/data-project/duplicates/${props.dataProjectId}`
            : `/app/duplicates/${props.system.id}`
        }
        trend={props.systemDetails.trend.duplicates}
        showDistinctContacts={props.showDistinctContacts}
        dataProjectId={props.dataProjectId}
        systemId={props.system?.id}
      />
    </>
  );
}
